import { cloneDeep } from 'lodash-es'


export interface ITreeItem {
  id: number
  name: string
  children?: ITreeItem[]
  [key: string]: any
}

interface ITreeItemMatchCriteria {
  itself: boolean
  children: boolean
}

function checkItemRecursive(
  item: ITreeItem,
  searchCriteria: (item: ITreeItem) => boolean,
  matches: Map<ITreeItem['id'], ITreeItemMatchCriteria>,
): boolean {
  const itself = searchCriteria(item)
  let children = false
  item.children?.forEach((item) => {
    if (checkItemRecursive(item, searchCriteria, matches)) {
      children = true // if compute it like `||=` some results will be lost due to optimization
    }
  })

  if (itself || children) {
    matches.set(item.id, { itself, children })
  }

  return itself || children
}

function copyTreeItemWithoutChildren(item: ITreeItem): ITreeItem {
  const newItem = Object.assign({}, item)
  delete newItem.children
  return cloneDeep(newItem)
}

// create new tree from original by criteria.
// if node not match criteria, but contains matched (grand)children, it will be left.
// result - tree of new objects that may be modified without changing original objects
function extractTree(
  items: ITreeItem[],
  includeItemCriteria: (item: ITreeItem) => ITreeItemMatchCriteria,
): ITreeItem[] {
  const res: ITreeItem[] = []

  items.forEach((item) => {
    const include = includeItemCriteria(item)

    if (include.itself || include.children) {
      // copy object only if it's about to include in new tree,
      // (copy without children, otherwise we'll destroy structure)
      const newItem = copyTreeItemWithoutChildren(item)
      res.push(newItem)

      if (include.children && item.children) {
        newItem.children = extractTree(item.children, includeItemCriteria)
      }
    }
  })

  return res
}

// search by tree with creation of new tree
// result - tree of new objects that may be modified without changing original objects
export function search(
  rootItems: ITreeItem[],
  searchCriteria: (item: ITreeItem) => boolean,
): {
    tree: ITreeItem[]
    matches: Map<ITreeItem['id'], ITreeItemMatchCriteria>
  } {
  const matches = new Map<ITreeItem['id'], ITreeItemMatchCriteria>()
  rootItems.forEach((item) => checkItemRecursive(item, searchCriteria, matches))

  const tree = extractTree(
    rootItems,
    (item: ITreeItem): ITreeItemMatchCriteria => {
      const match = matches.get(item.id)
      return match ?? { itself: false, children: false }
    },
  )

  return { tree, matches }
}

// helper method for text search by tree
export function textSearchCriteria(
  query: string,
  fields: string[] = ['name'],
): (item: ITreeItem) => boolean {
  // (maybe, we should use searchQueryToRegExps or split words at least)
  query = query.toLocaleLowerCase()
  return (item: ITreeItem) => {
    return fields.some((field) => item[field] && item[field].toLocaleLowerCase().includes(query))
  }
}

// helper method to get all item ids from tree
export function getAllIds(items: ITreeItem[]): number[] {
  const res: number[] = []
  items.forEach((item) => {
    res.push(item.id)

    if (item.children) {
      res.push(...getAllIds(item.children))
    }
  })
  return res
}
